import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { XCircleFill } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import { OnScreenHelpItems } from "../../../configs/assistant/OnScreenHelpItems";
import Authentication from "../../../libs/api/services/Authentication";
import CommonFunctionsContext from "../../../screens/common/CommonFunctionsProvider";

function filterPermittedSections(sections) {
    const roles = Authentication.getFacilityRoles?.() || [];
    return sections.reduce((acc, sec) => {
        const permittedItems = sec.items.filter(i => {
            if (!i.permissions) return true;
            return i.permissions.some(p => roles.includes(p));
        });
        if (permittedItems.length > 0) {
            acc.push({ section: sec.section, items: permittedItems });
        }
        return acc;
    }, []);
}

export default function OnScreenAssist({ section }) {
    const { helpRefs } = useContext(CommonFunctionsContext);
    const searchRef = useRef();
    const nav = useNavigate();
    const [searchMode, setSearchMode] = useState(true);
    const [show, setShow] = useState(false);
    const [selected, setSelected] = useState();
    const [items, setItems] = useState();
    const [filtered, setFiltered] = useState();
    const [sideNotes, setSideNotes] = useState([]);

    // Reset highlights and selection
    const resetHighlights = useCallback(() => {
        if (helpRefs?.current) {
            Object.values(helpRefs.current).forEach(refObj => {
                if (refObj.current) {
                    refObj.current.className = refObj.current.className
                        .replace("flash", "")
                        .replace("assist-highlight", "");
                }
            });
        }
        setSelected();
    }, [helpRefs]);

    // Handle navigation or other actions
    const handleAdditionalAction = useCallback((action, value, params) => {
        if (action === "location") nav(value, { state: params || {} });
    }, [nav]);

    // Highlight referenced element
    const handleRefAction = useCallback((secref, i, x) => {
        resetHighlights();
        setSelected(`s-${i}-${x}`);
        if (!secref) return;
        if (secref.action === "highlight" && helpRefs?.current?.[secref.ref]?.current) {
            const r = helpRefs.current[secref.ref].current;
            r.className = r.className + " assist-highlight flash";
        }
    }, [helpRefs, resetHighlights]);

    // Search by tag
    const handleSearchByTag = useCallback(e => {
        const val = e.target.value?.toLowerCase() || "";
        if (!val) {
            setFiltered(items);
            return;
        }
        const results = [];
        items?.forEach(section => {
            section.items.forEach(item => {
                if (!item.tags) return;
                const score = item.tags.reduce((acc, t) => acc + (val.includes(t) ? 1 : 0), 0);
                if (score > 0) results.push({ score, item });
            });
        });
        setFiltered([
            {
                section: "Results",
                items: results.sort((a, b) => b.score - a.score).map(i => i.item)
            }
        ]);
    }, [items]);

    // Backtrack side notes
    const handleBackTrack = useCallback(() => {
        if (!sideNotes?.length) {
            setFiltered(items);
            return;
        }
        const notes = [...sideNotes];
        notes.pop();
        setSideNotes(notes);
        if (notes.length && notes[notes.length - 1]) {
            setFiltered([notes[notes.length - 1]]);
        } else {
            setFiltered(items);
        }
    }, [sideNotes, items]);

    // Handle side note navigation
    const handleSideNote = useCallback((item, section) => {
        if (section.extra.section.type === "sub-section") {
            setSideNotes([...sideNotes, undefined, { section: "", items: [item] }]);
            const found = items.find(s => s.section === section.extra.section.module);
            setFiltered([
                {
                    section: "",
                    items: found ? found.items.filter(x => x.heading === section.extra.section.sectionName) : []
                }
            ]);
        }
    }, [sideNotes, items]);

    // Set search mode based on section
    useEffect(() => {
        setSearchMode(section !== "Dashboard");
    }, [section]);

    // Set items based on section and search mode
    useEffect(() => {
        if (OnScreenHelpItems[section] && searchMode) {
            setItems(filterPermittedSections([
                { section, items: OnScreenHelpItems[section].items }
            ]));
        } else {
            const allSections = Object.keys(OnScreenHelpItems).map(k => ({
                section: k,
                items: OnScreenHelpItems[k].items
            }));
            setItems(filterPermittedSections(allSections));
        }
    }, [section, searchMode]);

    // Update filtered when items change
    useEffect(() => {
        if (items) setFiltered(items);
    }, [items]);

    // Reset highlights when closing, focus search when opening
    useEffect(() => {
        if (!show) resetHighlights();
        else searchRef.current?.focus();
    }, [show, resetHighlights]);

    // Render
    return (
        items && items.length > 0 && (
            <div
                style={{ bottom: 0, right: 0, zIndex: 14000 }}
                className="position-absolute m-2 d-flex flex-column"
            >
                <div className={
                    `bg-dark rounded shadow position-relative p-3 ${show ? "hint-background" : "d-none"}`
                }>
                    <div
                        onClick={() => setShow(!show)}
                        style={{ right: "-5px", top: "-5px", filter: "drop-shadow(1px 2px 3px rgb(200, 200, 200))" }}
                        className="text-secondary position-absolute"
                    >
                        <XCircleFill size={28} />
                    </div>
                    <div style={{ maxHeight: "75vh", height: "75vh" }}>
                        <div>
                            <input
                                ref={searchRef}
                                onChange={handleSearchByTag}
                                className="form-control form-control-sm"
                                placeholder="Search"
                            />
                        </div>
                        <div className="text-center">
                            {section !== "Dashboard" && section !== "Authentication" && (
                                <button
                                    onClick={() => setSearchMode(sm => !sm)}
                                    className="btn btn-xs text-light"
                                >
                                    Show {`${searchMode ? "All" : section}`} Help
                                </button>
                            )}
                            {sideNotes?.length > 0 && (
                                <button
                                    onClick={handleBackTrack}
                                    className="btn btn-xs text-light"
                                >
                                    Back
                                </button>
                            )}
                        </div>
                        <div style={{ maxHeight: "calc(75vh - 50px)", overflow: "auto", position: "relative" }}>
                            {filtered && filtered.length > 0 ? (
                                filtered.map((sect, i) => (
                                    <div key={`OSI-S-F-${i}`}>
                                        <div><b>{sect.section}</b></div>
                                        <div>
                                            {sect.items.map((item, j) => (
                                                <ToggleAssistantSection
                                                    key={`ToggleAssistantSection-${j}`}
                                                    item={item}
                                                    i={j}
                                                    selected={selected}
                                                    _handleRefAction={handleRefAction}
                                                    _handleAdditionalAction={handleAdditionalAction}
                                                    _handleSideNote={handleSideNote}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center">Sorry, there are no entries matching your search criteria</div>
                            )}
                        </div>
                    </div>
                </div>
                <FontAwesomeIcon
                    style={{ filter: "drop-shadow(1px 2px 3px rgb(200, 200, 200))" }}
                    onClick={() => setShow(!show)}
                    className="align-self-end m-2"
                    size="3x"
                    icon={faQuestionCircle}
                />
            </div>
        )
    );
}

const ToggleAssistantSection = ({ item, i, selected, _handleRefAction, _handleAdditionalAction, _handleSideNote }) => {
    const [expanded, setExpanded] = useState(false);
    return (
        <div>
            <div onClick={() => setExpanded(e => !e)}>
                <small className="pe-1 text-light">{item.heading}</small>
            </div>
            {expanded && (
                <>
                    {item.description && (
                        <small dangerouslySetInnerHTML={{ __html: item.description }} />
                    )}
                    {item.sections && item.sections.map((sec, x) => (
                        <div
                            key={`s-a-${x}`}
                            className={`${selected === `s-${i}-${x}` ? "assist-highlight" : ""} ${sec.styleAs ? `assist-style-${sec.styleAs}` : ""}`}
                            onClick={() => _handleRefAction(sec.ref, i, x)}
                        >
                            <small dangerouslySetInnerHTML={{ __html: sec.description }} />
                            {sec.extra?.type === "Side Note" && sec.extra.section.type === "sub-section" && (
                                <div>
                                    <button
                                        onClick={e => {
                                            e.stopPropagation();
                                            _handleSideNote(item, sec);
                                        }}
                                        className="btn btn-xs text-primary"
                                    >
                                        {sec.extra.section.humanReadable}
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                    <div>
                        {item.options && item.options.map((opt, idx) => (
                            <button
                                key={`OSI-OPT-${idx}-${opt.option}`}
                                onClick={() => _handleAdditionalAction(opt.action.type, opt.action.value, opt.action.params)}
                                className="btn text-primary btn-xs"
                            >
                                {opt.option}
                            </button>
                        ))}
                    </div>
                    <hr />
                </>
            )}
        </div>
    );
};